/**
 * ReservaSlideOver — Panel lateral derecho con el detalle completo de una reserva.
 * Pestañas internas: Datos · Viajeros · Pagos · Auditoría.
 * Acciones rápidas: cambiar estado, generar link Bold, agregar pago/viajero.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Chip, Stack, Alert, CircularProgress,
  IconButton, TextField, MenuItem, Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaymentIcon from '@mui/icons-material/Payment';
import PaymentsIcon from '@mui/icons-material/Payments';
import SavingsIcon from '@mui/icons-material/Savings';
import CalculateIcon from '@mui/icons-material/Calculate';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LinkIcon from '@mui/icons-material/Link';
import GroupAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import useToast from '../common/useToast';
import ReagendarDialog from './ReagendarDialog';
import EditarReservaSheet from './EditarReservaSheet';
import EditarMovimientoDialog from './EditarMovimientoDialog';
import { movimientoCorregible } from './ElegirPagoDialog';
import RegistrarPagoAgenciaDialog from './RegistrarPagoAgenciaDialog';
import CobroMuelleCxCDialog from './CobroMuelleCxCDialog';
import AplicarSaldoFavorDialog from './AplicarSaldoFavorDialog';
import BorrarReservaDialog from './BorrarReservaDialog';
import ReliquidarNetoDialog from './ReliquidarNetoDialog';
import CambiarAgenciaDialog from './CambiarAgenciaDialog';
import TimelineReserva from './TimelineReserva';
import Tooltip from '@mui/material/Tooltip';
import EstadoChip, { ESTADOS } from './shared/EstadoChip';
import EstadoEmbarqueChip from './shared/EstadoEmbarqueChip';
import SaldoDestinoChip from './shared/SaldoDestinoChip';
import VoucherSubsanarChip from './shared/VoucherSubsanarChip';
import LagRegistroChip from './shared/LagRegistroChip';
import {
  API_BASE, getAuthHeaders, formatPrecio, formatFechaLarga, formatHora, fmtPax,
} from './shared/format';

const ESTADO_OPCIONES = ESTADOS;

export default function ReservaSlideOver({ reservaId, onClose, onUpdated, zIndex }) {
  const { toast, ToastContainer } = useToast();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viajeros, setViajeros] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [estadoOpen, setEstadoOpen] = useState(false);
  const [reagendarOpen, setReagendarOpen] = useState(false);
  const [editarOpen, setEditarOpen] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [motivoCancel, setMotivoCancel] = useState('');
  const [viajeroDialog, setViajeroDialog] = useState(null); // null | { editando, form }
  const [confirmDel, setConfirmDel] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}`, { headers: getAuthHeaders() });
      if (r.ok) setData(await r.json());
    } catch (e) { toast.error('Error cargando reserva'); }
    setLoading(false);
  }, [reservaId]); // eslint-disable-line

  const cargarViajeros = useCallback(async () => {
    const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/viajeros`, { headers: getAuthHeaders() });
    if (r.ok) { const d = await r.json(); setViajeros(d.viajeros || []); }
  }, [reservaId]);

  // Vista unificada: vouchers + pagos contables + pagos pasarela + liquidación.
  // El endpoint /movimientos consolida las 4 fuentes y las ordena cronológicamente.
  const [movimientos, setMovimientos] = useState({ movimientos: [], totales: {} });
  const cargarMovimientos = useCallback(async () => {
    const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/movimientos`, { headers: getAuthHeaders() });
    if (r.ok) {
      const d = await r.json();
      setMovimientos(d);
      // Compat: dejo `setPagos` apuntando a los pagos_pasarela para no romper otros
      // lugares que aún lean `pagos` (ej. botón "abrir link" Bold). El nuevo TabPagos
      // usa `movimientos.movimientos`.
      setPagos((d.movimientos || []).filter(m => m.tipo === 'pago_pasarela').map(m => ({
        id: m.metadata?.pago_id, monto: m.monto, estado_pago: m.estado,
        pasarela: m.subtipo, payment_url: m.payment_url,
        created_at: m.fecha, metodo_pago_usado: m.metadata?.metodo_pago_usado,
      })));
    }
  }, [reservaId]);

  // Evidencia de checkin (fotos del muelle). Se carga bajo demanda cuando el
  // usuario abre el tab. Las fotos son sensibles y viven en un endpoint aparte.
  const [checkin, setCheckin] = useState({ evidencias: [], total: 0 });
  const cargarCheckin = useCallback(async () => {
    const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/checkin-evidencia`, { headers: getAuthHeaders() });
    if (r.ok) setCheckin(await r.json());
  }, [reservaId]);

  useEffect(() => {
    cargar(); cargarViajeros(); cargarMovimientos(); cargarCheckin();
  }, [cargar, cargarViajeros, cargarMovimientos, cargarCheckin]);

  const handleCambiarEstado = async () => {
    try {
      const body = { estado: nuevoEstado };
      if (nuevoEstado === 'cancelada') body.motivo_cancelacion = motivoCancel || null;
      const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/estado`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
      toast.success('Estado actualizado');
      setEstadoOpen(false);
      cargar(); onUpdated?.();
    } catch (e) { toast.error('Error de red'); }
  };

  const handleGenerarLink = async () => {
    try {
      const r = await fetch(`${API_BASE}/turismo/pagos/generar-link/${reservaId}`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ pasarela: 'bold' }),
      });
      const d = await r.json();
      if (d.success && d.payment_url) {
        navigator.clipboard?.writeText(d.payment_url).catch(() => {});
        toast.success('Link generado y copiado al portapapeles');
        cargarMovimientos();
      } else {
        toast.error(d.error || 'Error generando link');
      }
    } catch (e) { toast.error('Error de red'); }
  };

  const handleGuardarViajero = async () => {
    const { editando, form } = viajeroDialog;
    const body = { ...form };
    if (!body.fecha_nacimiento) delete body.fecha_nacimiento;
    const url = editando
      ? `${API_BASE}/turismo/viajeros/${editando.id}`
      : `${API_BASE}/turismo/reservaciones/${reservaId}/viajeros`;
    const method = editando ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(body) });
    if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); return; }
    setViajeroDialog(null);
    cargarViajeros();
    toast.success(editando ? 'Viajero actualizado' : 'Viajero agregado');
  };

  const handleEliminarViajero = async () => {
    if (!confirmDel) return;
    await fetch(`${API_BASE}/turismo/viajeros/${confirmDel.id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    setConfirmDel(null);
    cargarViajeros();
    toast.success('Viajero eliminado');
  };

  const r = data;

  // Editable solo si: creada hoy + no embarcada + no cancelada + sin liquidación.
  // Mismas reglas que aplica el backend; las duplicamos para mostrar tooltip
  // explicativo sin tener que hacer roundtrip.
  const _hoy = (() => {
    const d = new Date(); const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();
  const _creadaDia = r?.created_at ? String(r.created_at).slice(0, 10) : null;
  const _creadaHoy = !!_creadaDia && _creadaDia === _hoy;
  const _embarcado = r?.estado_embarque === 'embarcado';
  const _cancelada = r?.estado === 'cancelada';
  const _conLiquidacion = !!r?.liquidacion_id;
  const puedeEditar = !!r && _creadaHoy && !_embarcado && !_cancelada && !_conLiquidacion;
  // Rol del usuario logueado — para gate del botón "Cambiar estado" (solo admin/superadmin).
  const _userRole = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
    catch { return ''; }
  })();
  const puedeCambiarEstado = ['admin', 'superadmin'].includes(_userRole);
  // Borrar se lleva pagos, vouchers y cupos por delante: mismos roles que
  // acepta el endpoint, y nunca coordinador.
  const puedeBorrar = ['admin', 'superadmin'].includes(_userRole);
  const [borrarOpen, setBorrarOpen] = useState(false);
  let tooltipNoEditable = '';
  if (!_creadaHoy)         tooltipNoEditable = `Solo se puede editar el día de creación (${_creadaDia || 's/d'}). Hoy es ${_hoy}.`;
  else if (_embarcado)     tooltipNoEditable = 'Esta reserva ya tiene check-in. Deshacelo primero desde el muelle.';
  else if (_cancelada)     tooltipNoEditable = 'Una reserva cancelada no se edita. Creá una nueva.';
  else if (_conLiquidacion) tooltipNoEditable = 'La comisión ya fue liquidada con la agencia. No se puede modificar.';

  return (
    <>
      <SlideOver
        open
        zIndex={zIndex}
        onClose={onClose}
        variant="wide"
        title={r ? `${r.codigo_reserva}` : 'Cargando…'}
        subtitle={r ? r.nombre_cliente : ''}
        icon={<ConfirmationNumberIcon />}
        footer={
          <>
            {/* Cerrar se va a la izquierda —es salir, no una acción sobre la
                reserva— y el resto queda agrupado a la derecha. */}
            <Button onClick={onClose} size="small" sx={{ ...BTN_PIE, mr: 'auto', color: '#64748b' }}>
              Cerrar
            </Button>
            {r && (
              <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                onClick={() => window.open(`${API_BASE}/turismo/reservaciones/${reservaId}/voucher.pdf`, '_blank')}
                sx={{ ...BTN_PIE, ...BTN_SECUNDARIO }}>
                Voucher PDF
              </Button>
            )}
            {r && r.tipo_reservacion !== 'alquiler'
                && !['cancelada', 'completada', 'no_show'].includes(r.estado)
                && r.estado_embarque !== 'embarcado' && (
              <Button variant="outlined" size="small" startIcon={<EventRepeatIcon />}
                onClick={() => setReagendarOpen(true)}
                sx={{ ...BTN_PIE, ...BTN_SECUNDARIO }}>
                Reagendar
              </Button>
            )}
            {r && (
              <Tooltip title={puedeEditar ? 'Editar esta reserva' : tooltipNoEditable}>
                <span>
                  <Button variant="contained" size="small" disableElevation startIcon={<EditIcon />}
                    onClick={() => setEditarOpen(true)} disabled={!puedeEditar}
                    sx={{ ...BTN_PIE,
                          background: puedeEditar ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' : undefined }}>
                    Editar
                  </Button>
                </span>
              </Tooltip>
            )}
            {/* Cambiar estado: operación sensible (puede cancelar/confirmar/completar).
                Solo admin/superadmin — comerciales/intermediarios no deben cambiar
                el estado manualmente porque afecta cupos, comisiones y auditoría. */}
            {puedeCambiarEstado && (
              <Button variant="outlined" size="small" startIcon={<SwapHorizIcon />}
                onClick={() => { setNuevoEstado(r?.estado || ''); setEstadoOpen(true); }}
                sx={{ ...BTN_PIE, ...BTN_SECUNDARIO }}>
                Cambiar estado
              </Button>
            )}
            {/* Borrar va de último, con la misma forma que los demás —outlined,
                mismo alto y mismo tipo de letra— y solo el color lo distingue.
                Una reserva embarcada ya viajó: ahí el botón queda deshabilitado
                y el tooltip dice por qué, igual que hace Editar. */}
            {r && puedeBorrar && (
              <Tooltip title={_embarcado
                ? 'Esta reserva ya está embarcada: sus pasajeros viajaron y no se puede borrar.'
                : 'Borrar la reserva y deshacer su movimiento financiero'}>
                <span>
                  <Button variant="outlined" size="small" startIcon={<DeleteIcon />}
                    onClick={() => setBorrarOpen(true)} disabled={_embarcado}
                    sx={{ ...BTN_PIE, borderColor: '#fca5a5', color: '#b91c1c',
                          '&:hover': { borderColor: '#b91c1c', bgcolor: '#fef2f2' } }}>
                    Borrar
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        }
      >
        {loading || !r ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {/* Cabecera con estado de reserva + estado de embarque + total */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
              <EstadoEmbarqueChip estadoEmbarque={r.estado_embarque} />
              <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                saldoACargoDe={r.saldo_a_cargo_de}
                                saldoMuelleMonto={r.saldo_muelle_monto}
                                cxcAgenciaMonto={r.cxc_agencia_monto}
                                formaPagoAgencia={r.forma_pago_agencia} />
              <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
              {/* Indicador de oportunidad de registro (Bloque 2 · lag comercial).
                  Solo admin/superadmin/administrativo pueden justificar el lag —
                  comerciales lo ven en modo lectura. */}
              <LagRegistroChip
                reservaId={r.id}
                lagRegistro={r.lag_registro}
                puedeJustificar={['admin', 'superadmin', 'administrativo']
                  .includes((localStorage.getItem('user_role') || '').toLowerCase())}
                onUpdated={() => { cargar(); onUpdated?.(); }}
              />
              {r.agencia_nombre && (
                <Chip size="small" label={`Agencia: ${r.agencia_nombre}`}
                  sx={{ bgcolor: '#fdf2f8', color: '#be185d', fontWeight: 600, fontSize: 11 }} />
              )}
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0d9488' }}>
                {formatPrecio(r.precio_total, r.moneda)}
              </Typography>
            </Box>
            {Number(r.saldo_pendiente || 0) > 0 && (
              <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
                Saldo pendiente: <b>{formatPrecio(r.saldo_pendiente, r.moneda)}</b>
              </Alert>
            )}

            <Tabs value={tab} onChange={(_, v) => setTab(v)}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: '1px solid #e2e8f0',
                    '& .MuiTabs-indicator': { backgroundColor: '#0d9488' } }}>
              <Tab label="Datos" sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
              <Tab label={`Viajeros (${viajeros.length}/${r.total_pasajeros || 0})`}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
              <Tab label={`Movimientos (${(movimientos.movimientos || []).length})`} sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
              <Tab label={`Evidencia (${checkin.total || 0})`} sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
              <Tab label="Timeline" sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, minHeight: 40 }} />
            </Tabs>

            {tab === 0 && <TabDatos r={r} onFacturacionGuardada={() => { cargar(); onUpdated?.(); }} onToast={toast} />}
            {tab === 1 && (
              <TabViajeros
                viajeros={viajeros}
                onAdd={() => setViajeroDialog({ editando: null, form: emptyViajero() })}
                onEdit={(v) => setViajeroDialog({ editando: v, form: toForm(v) })}
                onDelete={(v) => setConfirmDel(v)}
              />
            )}
            {tab === 2 && (
              <TabMovimientos
                data={movimientos}
                reserva={r}
                onGenerarLink={handleGenerarLink}
                onReload={() => { cargarMovimientos(); cargar(); onUpdated?.(); }}
                onToast={toast}
              />
            )}
            {tab === 3 && <TabEvidencia data={checkin} onReload={cargarCheckin} onToast={toast} />}
            {tab === 4 && <TimelineReserva reservaId={reservaId} />}
          </>
        )}
      </SlideOver>

      {/* Diálogo cambiar estado */}
      <SlideOver
        open={estadoOpen}
        onClose={() => setEstadoOpen(false)}
        title="Cambiar estado"
        subtitle={r?.codigo_reserva}
        icon={<EditIcon />}
        footer={
          <>
            <Button onClick={() => setEstadoOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
            <Button variant="contained" onClick={handleCambiarEstado} disableElevation
              sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              Aplicar
            </Button>
          </>
        }
      >
        <Stack spacing={1.5}>
          <TextField select size="small" label="Nuevo estado" value={nuevoEstado}
            onChange={e => setNuevoEstado(e.target.value)}>
            {ESTADO_OPCIONES.map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          {nuevoEstado === 'cancelada' && (
            <TextField size="small" multiline minRows={2} label="Motivo (opcional)"
              value={motivoCancel} onChange={e => setMotivoCancel(e.target.value)} />
          )}
        </Stack>
      </SlideOver>

      {/* Diálogo viajero */}
      {viajeroDialog && (
        <ViajeroDialog
          editando={viajeroDialog.editando}
          form={viajeroDialog.form}
          onChange={(form) => setViajeroDialog(prev => ({ ...prev, form }))}
          onSave={handleGuardarViajero}
          onClose={() => setViajeroDialog(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={handleEliminarViajero}
        title="¿Eliminar viajero?"
        message={confirmDel ? `${confirmDel.nombre_completo}. Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar"
        severity="danger"
      />

      <BorrarReservaDialog
        open={borrarOpen}
        onClose={() => setBorrarOpen(false)}
        reserva={r}
        onToast={toast}
        onBorrada={() => { onUpdated?.(); onClose?.(); }}
      />

      {reagendarOpen && r && (
        <ReagendarDialog
          reserva={r}
          onClose={() => setReagendarOpen(false)}
          onReagendada={() => { cargar(); onUpdated?.(); }}
        />
      )}

      {editarOpen && r && (
        <EditarReservaSheet
          reserva={r}
          onClose={() => setEditarOpen(false)}
          onSuccess={(res) => {
            setEditarOpen(false);
            toast.success('Reserva editada' + (res?.cambios?.length ? ` (${res.cambios.length} campo${res.cambios.length > 1 ? 's' : ''})` : ''));
            cargar(); onUpdated?.();
          }}
          onError={(e) => {
            if (e?.status === 409) {
              setEditarOpen(false);
              toast.error(e?.message || 'Otra persona acaba de modificar esta reserva. Refrescando…');
              cargar();
              return;
            }
            toast.error(e?.message || 'Error');
          }}
        />
      )}

      <ToastContainer />
    </>
  );
}

// ----------------------------------------------------------------------------
// Sub-componentes
// ----------------------------------------------------------------------------

function TabDatos({ r, onFacturacionGuardada, onToast }) {
  // El endpoint /reservaciones/{id} devuelve la salida anidada en `r.salida`,
  // mientras que la lista la devuelve plana. Soportamos ambas formas.
  const fecha = r.salida_fecha || r.salida?.fecha;
  const horaSalida = r.salida_hora || r.salida?.hora_salida;
  const horaRegreso = r.salida?.hora_regreso;
  const paquete = r.paquete_nombre || r.salida?.paquete_nombre || r.alquiler_nombre;
  const destino = r.destino_nombre || r.salida?.destino_nombre;
  const embarcacion = r.embarcacion_alquiler || r.salida?.embarcacion_nombre;
  const puntoEmbarque = r.punto_embarque || r.salida?.punto_embarque;
  return (
    <Stack spacing={1.5}>
      <Section title="Salida">
        <Row label="Fecha" value={formatFechaLarga(fecha)} />
        {horaSalida && (
          <Row label="Hora"
            value={`${formatHora(horaSalida)}${horaRegreso ? ` → ${formatHora(horaRegreso)}` : ''}`} />
        )}
        <Row label="Paquete / Tour" value={paquete || '—'} />
        {destino && <Row label="Destino" value={destino} />}
        {puntoEmbarque && <Row label="Embarque" value={puntoEmbarque} />}
        {embarcacion && <Row label="Embarcación" value={embarcacion} />}
      </Section>

      <Section title="Cliente">
        <Row label="Nombre" value={r.nombre_cliente} />
        <Row label="WhatsApp" value={r.numero_whatsapp || '—'} />
        {r.correo && <Row label="Correo" value={r.correo} />}
      </Section>

      <Section title="Pasajeros">
        <Row label="Total" value={`${r.total_pasajeros || 0} pax`} />
        <Row label="Desglose" value={fmtPax(r)} />
        {r.cortesias > 0 && (
          <Row label="Cortesías"
            value={
              (r.cortesias_adultos || 0) + (r.cortesias_ninos || 0) > 0
                ? `${r.cortesias_adultos || 0} adulto${r.cortesias_adultos === 1 ? '' : 's'} · ${r.cortesias_ninos || 0} niño${r.cortesias_ninos === 1 ? '' : 's'}`
                : `${r.cortesias}`
            } />
        )}
        {r.cortesias_autorizada_por_nombre && (
          <Row label="Autorizada por" value={r.cortesias_autorizada_por_nombre} />
        )}
        {r.cortesias_motivo && (
          <Row label="Motivo cortesía" value={r.cortesias_motivo} />
        )}
      </Section>

      <Section title="Comercial">
        {r.agencia_nombre && <Row label="Agencia" value={r.agencia_nombre} />}
        {r.canal_reserva && <Row label="Canal" value={r.canal_reserva} />}
        {Number(r.comision_valor || 0) > 0 && (
          <Row label="Comisión" value={`${formatPrecio(r.comision_valor)} (${r.comision_porcentaje}%)`} />
        )}
        {Number(r.descuento_comercial || 0) > 0 && (
          <Row label="Descuento" value={formatPrecio(r.descuento_comercial)} />
        )}
        {r.descuento_autorizado_por_nombre && (
          <Row label="Autorizado por" value={r.descuento_autorizado_por_nombre} />
        )}
      </Section>

      {(r.notas || r.requerimientos_especiales) && (
        <Section title="Notas">
          {r.notas && <Typography sx={{ fontSize: 12, color: '#475569' }}>{r.notas}</Typography>}
          {r.requerimientos_especiales && (
            <Typography sx={{ fontSize: 12, color: '#9a3412', mt: 0.5 }}>
              ⚠ {r.requerimientos_especiales}
            </Typography>
          )}
        </Section>
      )}

      <SeccionFacturacion reserva={r} onGuardada={onFacturacionGuardada} onToast={onToast} />
    </Stack>
  );
}

/**
 * SeccionFacturacion — carga opcional del número y fecha de factura electrónica.
 * Editable siempre (la restricción por estado la decide operativa, no el sistema).
 * Detecta si ya hay datos cargados o no y ajusta la etiqueta del botón.
 */
function SeccionFacturacion({ reserva, onGuardada, onToast }) {
  const [numero, setNumero] = useState(reserva.numero_factura || '');
  const [fecha, setFecha] = useState(reserva.fecha_factura || '');
  const [saving, setSaving] = useState(false);

  // Sincroniza el form cuando la reserva se re-cargar (después de guardar).
  useEffect(() => {
    setNumero(reserva.numero_factura || '');
    setFecha(reserva.fecha_factura || '');
  }, [reserva.numero_factura, reserva.fecha_factura]);

  const numeroLimpio = (numero || '').trim();
  const yaCargada = !!reserva.numero_factura;
  const sinCambios = numeroLimpio === (reserva.numero_factura || '') && (fecha || '') === (reserva.fecha_factura || '');
  const ambosVacios = !numeroLimpio && !fecha;
  const incompleto = (!!numeroLimpio) !== (!!fecha);   // XOR — uno sí y otro no
  const puedeGuardar = !saving && !sinCambios && !incompleto;

  const handleGuardar = async () => {
    setSaving(true);
    try {
      const body = ambosVacios
        ? { numero_factura: null, fecha_factura: null }
        : { numero_factura: numeroLimpio, fecha_factura: fecha };
      const res = await fetch(`${API_BASE}/turismo/reservaciones/${reserva.id}/facturacion`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        onToast?.error?.(d.detail || 'Error guardando facturación');
        return;
      }
      onToast?.success?.(ambosVacios ? 'Datos de factura limpiados' : 'Factura guardada');
      onGuardada?.();
    } catch {
      onToast?.error?.('Error de red');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Facturación electrónica">
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 1, mb: 1 }}>
        <TextField size="small" label="Número de factura" value={numero}
          onChange={e => setNumero(e.target.value)}
          placeholder="Ej: FE-1234"
          inputProps={{ maxLength: 50 }}
          sx={{ bgcolor: '#fff' }} />
        <TextField size="small" type="date" label="Fecha de factura" value={fecha}
          onChange={e => setFecha(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ bgcolor: '#fff' }} />
      </Box>
      {incompleto && (
        <Typography sx={{ fontSize: 10.5, color: '#dc2626', mb: 0.75 }}>
          Debes cargar tanto el número como la fecha (o dejar ambos vacíos para limpiar).
        </Typography>
      )}
      {!yaCargada && ambosVacios && (
        <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mb: 0.75 }}>
          Solo cargar si el cliente pidió factura. Es opcional por reserva.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" variant="contained" disableElevation
          disabled={!puedeGuardar}
          onClick={handleGuardar}
          sx={{
            textTransform: 'none', fontSize: 11, fontWeight: 700,
            background: puedeGuardar ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' : undefined,
          }}>
          {saving ? 'Guardando…' : (yaCargada && ambosVacios ? 'Limpiar' : (yaCargada ? 'Actualizar' : 'Guardar'))}
        </Button>
      </Box>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0d9488', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
        {title}
      </Typography>
      <Box sx={{
        p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0',
      }}>
        {children}
      </Box>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
      <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

function TabViajeros({ viajeros, onAdd, onEdit, onDelete }) {
  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>Lista de viajeros registrados</Typography>
        <Button size="small" startIcon={<GroupAddIcon />} onClick={onAdd}
          sx={{ textTransform: 'none', fontSize: 12, color: '#0d9488' }}>
          Agregar
        </Button>
      </Box>
      {viajeros.length === 0 && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          No hay viajeros registrados. Agrega al menos el contacto principal.
        </Alert>
      )}
      {viajeros.map(v => (
        <Box key={v.id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
                              display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
              {v.nombre_completo}
              {v.es_contacto_principal && (
                <Chip label="Principal" size="small" sx={{ ml: 1, height: 16, fontSize: 9, bgcolor: '#dbeafe', color: '#1e40af' }} />
              )}
              {v.es_menor && (
                <Chip label="Menor" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9, bgcolor: '#fef3c7', color: '#92400e' }} />
              )}
            </Typography>
            {(v.tipo_documento || v.numero_documento) && (
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                {v.tipo_documento} {v.numero_documento}
              </Typography>
            )}
            {v.telefono_contacto && (
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>📱 {v.telefono_contacto}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => onEdit(v)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
          <IconButton size="small" onClick={() => onDelete(v)} sx={{ color: '#ef4444' }}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
}

// El pie de la ficha: todos los botones con el mismo alto, la misma letra y
// el mismo icono. Solo el color separa la acción principal (Editar) y la
// destructiva (Borrar) del resto.
const BTN_PIE = {
  textTransform: 'none', fontWeight: 700, fontSize: 13,
  height: 34, px: 1.5, whiteSpace: 'nowrap',
  '& .MuiButton-startIcon > *': { fontSize: 17 },
};
const BTN_SECUNDARIO = { borderColor: '#cbd5e1', color: '#0f766e',
                         '&:hover': { borderColor: '#0d9488', bgcolor: '#f0fdfa' } };

// Mapa de iconos/colores por tipo y subtipo de movimiento
const TIPO_VISUAL = {
  voucher:        { icon: '🧾', label: 'Voucher',     color: '#0d9488', bg: '#f0fdfa' },
  pago_contable:  { icon: '💰', label: 'Pago',         color: '#3b82f6', bg: '#eff6ff' },
  pago_pasarela:  { icon: '🔗', label: 'Link pago',    color: '#8b5cf6', bg: '#faf5ff' },
  liquidacion:    { icon: '🏦', label: 'Liquidación',  color: '#be185d', bg: '#fdf2f8' },
  cxc_agencia:    { icon: '🧾', label: 'CxC agencia',  color: '#0891b2', bg: '#ecfeff' },
  reversion:      { icon: '↩️', label: 'Reversión',    color: '#dc2626', bg: '#fef2f2' },
  // Plata que la agencia transfirió de más y quedó esperando su próxima reserva
  saldo_favor_agencia: { icon: '💚', label: 'Saldo a favor', color: '#15803d', bg: '#f0fdf4' },
};
const ESTADO_COLOR = {
  aprobado:  { bg: '#f0fdf4', fg: '#166534' },
  pagado:    { bg: '#f0fdf4', fg: '#166534' },
  liquidada: { bg: '#fdf2f8', fg: '#be185d' },
  aplicado:  { bg: '#eff6ff', fg: '#1d4ed8' },
  disponible:{ bg: '#f0fdf4', fg: '#15803d' },
  pendiente: { bg: '#fef3c7', fg: '#92400e' },
  rechazado: { bg: '#fef2f2', fg: '#dc2626' },
  revertido: { bg: '#fef2f2', fg: '#dc2626' },
};

// Visual del estado de comisión — qué pasa con la plata que le toca a la agencia
const ESTADO_COMISION = {
  anticipada_total:   { color: '#16a34a', bg: '#f0fdf4', icon: '🟢', short: 'Anticipada' },
  liquidada:          { color: '#16a34a', bg: '#f0fdf4', icon: '✅', short: 'Liquidada' },
  anticipada_parcial: { color: '#f59e0b', bg: '#fffbeb', icon: '🟡', short: 'Parcial' },
  pendiente_total:    { color: '#dc2626', bg: '#fef2f2', icon: '🔴', short: 'Pendiente' },
  sin_comision:       { color: '#64748b', bg: '#f1f5f9', icon: '⚪', short: 'Venta directa' },
};

function TabMovimientos({ data, reserva, onGenerarLink, onReload, onToast }) {
  const movimientos = data?.movimientos || [];
  const t = data?.totales || {};
  const f = data?.desglose_financiero || {};
  const estado = ESTADO_COMISION[f.estado_comision] || ESTADO_COMISION.pendiente_total;
  const resumen = data?.resumen;
  const alertas = data?.alertas || [];
  const [accionLoading, setAccionLoading] = useState(null); // id del movimiento procesándose

  // Estado del dialog de edición manual (voucher / pago). Reemplaza el flujo
  // de "correcciones vía SQL directo" para los casos frecuentes: monto mal,
  // cuenta destino faltante, imagen que no se subió, pago fantasma, etc.
  const [editMov, setEditMov] = useState(null); // el movimiento a editar (o null)
  // "Llegó el pago": solo aplica cuando el saldo es una cuenta por cobrar de
  // la agencia. Ver el botón, más abajo, junto al renglón del CxC.
  const [pagoAgenciaOpen, setPagoAgenciaOpen] = useState(false);
  // Repartir el saldo: cuánto va a pagar de verdad el pasajero en el muelle y
  // qué pasa con la diferencia —CxC de la agencia, o cruce contra comisión—.
  // No registra pagos: el cobro vive en Muelle → Recepción.
  const [cobroMuelleOpen, setCobroMuelleOpen] = useState(false);
  // Saldo a favor de la agencia: plata que ya entregó y quedó sin repartir.
  // Se consulta al abrir la pestaña para saber si ofrecer el botón.
  const [saldoFavor, setSaldoFavor] = useState({ saldo_a_favor: 0, origenes: [] });
  const [aplicarFavorOpen, setAplicarFavorOpen] = useState(false);
  // Reliquidar: cambiar el neto por pasajero cuando la venta se pactó con la
  // tarifa anterior. Vive pegado al bloque de comisión, que es lo que mueve.
  const [reliquidarOpen, setReliquidarOpen] = useState(false);
  // Cambiar la agencia vendedora: la venta quedó a nombre de quien no la hizo.
  // Vive en el mismo bloque, que es donde se lee el nombre de la agencia.
  const [cambiarAgenciaOpen, setCambiarAgenciaOpen] = useState(false);

  // Y solo hasta el día del pasadía, ese día incluido. Desde el día siguiente
  // el pago deja de ser parte de la operación y pasa a ser cartera: se
  // registra por cuentas por cobrar, en Finanzas.
  const hoyBogota = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  // La fecha del pasadía viene anidada en `salida.fecha` en el detalle de la
  // reserva, y plana en `salida_fecha` en los listados. Se miran las dos: con
  // solo la plana, el detalle daba vacío y el botón desaparecía siempre.
  const diaSalida = (reserva?.salida?.fecha || reserva?.salida_fecha || '').slice(0, 10);
  const dentroDePlazo = !!diaSalida && diaSalida >= hoyBogota;

  // Corregir un movimiento se puede hasta tres días después del pasadía.
  // Pasado ese plazo la plata ya se concilió contra el extracto y cambiarle
  // la cuenta mueve cifras que alguien dio por buenas. El servidor lo frena
  // igual; acá el lápiz queda gris para que no se intente.
  const DIAS_PARA_CORREGIR = 3;
  const venceCorreccion = (() => {
    if (!diaSalida) return null;
    const [a, m, d] = diaSalida.split('-').map(Number);
    return new Date(a, m - 1, d + DIAS_PARA_CORREGIR);
  })();
  const puedeCorregirMovimiento = (() => {
    if (!venceCorreccion) return true;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    return hoy <= venceCorreccion;
  })();
  const _fmtDia = (f) => f?.toLocaleDateString('es-CO',
    { day: '2-digit', month: 'short', year: 'numeric' });
  const [cuentasDestino, setCuentasDestino] = useState([]);

  useEffect(() => {
    const agenciaId = reserva?.agencia_id;
    if (!agenciaId) { setSaldoFavor({ saldo_a_favor: 0, origenes: [] }); return; }
    fetch(`${API_BASE}/turismo/correcciones/agencias/${agenciaId}/saldo-a-favor`,
      { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSaldoFavor(d || { saldo_a_favor: 0, origenes: [] }))
      .catch(() => setSaldoFavor({ saldo_a_favor: 0, origenes: [] }));
  }, [reserva?.agencia_id, reserva?.saldo_pendiente]);

  useEffect(() => {
    const empresaId = reserva?.empresa_id;
    if (!empresaId) return;
    fetch(`${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${empresaId}`,
      { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => setCuentasDestino(Array.isArray(list) ? list : []))
      .catch(() => setCuentasDestino([]));
  }, [reserva?.empresa_id]);

  // Helper: user id + nombre del coordinador logueado, para auditoría.
  const getUserId = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u.id || u.user_id || null;
    } catch { return null; }
  };
  const getUserInfo = () => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const id = u.id || u.user_id || null;
      const nombre = u.name || u.nombre || u.display_name || u.email || null;
      return { id, nombre };
    } catch { return { id: null, nombre: null }; }
  };
  // Rol del usuario logueado — decide si puede corregir movimientos financieros.
  // Solo admin/superadmin pueden editar vouchers y pagos contables.
  const getUserRole = () => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
    catch { return ''; }
  };
  const puedeEditarMovimientos = ['admin', 'superadmin'].includes(getUserRole());
  // La cuenta de un PAGO la corrige todo el backoffice —mismo criterio que el
  // endpoint de correcciones—; el voucher sigue siendo de admin, porque su
  // edición va por otro endpoint que exige ese rol.
  const puedeCorregirCuentaPago = ['admin', 'superadmin', 'coordinador',
                                   'administrativo'].includes(getUserRole());
  // Reclasificar un saldo de muelle a cuenta por cobrar mueve deuda de una
  // agencia: mismos roles que acepta el endpoint de correcciones.
  const puedeCorregirSaldo = ['admin', 'superadmin', 'coordinador',
                              'administrativo'].includes(getUserRole());

  // Aprobar o rechazar un voucher pendiente. Reusa el endpoint existente
  // PATCH /turismo/vouchers/{id}/verificar (mismo que ya usa la vista de
  // vouchers, solo lo exponemos también desde aquí para que el coordinador
  // pueda decidir sin salir de la reserva).
  const handleVerificarVoucher = async (voucherId, nuevoEstado) => {
    let notas = null;
    if (nuevoEstado === 'rechazado') {
      // eslint-disable-next-line no-alert
      notas = window.prompt('¿Por qué se rechaza el voucher? (opcional)') || null;
    }
    setAccionLoading(`voucher-${voucherId}`);
    try {
      const res = await fetch(`${API_BASE}/turismo/vouchers/${voucherId}/verificar`, {
        method: 'PATCH', headers: getAuthHeaders(),
        body: JSON.stringify({ estado: nuevoEstado, notas_revision: notas, user_id: getUserId() }),
      });
      const body = await res.json();
      if (!res.ok) { onToast?.error?.(body.detail || 'Error procesando voucher'); return; }
      onToast?.success?.(nuevoEstado === 'aprobado' ? 'Voucher aprobado' : 'Voucher rechazado');
      onReload?.();
    } catch { onToast?.error?.('Error de red'); }
    finally { setAccionLoading(null); }
  };

  // Reconciliar manualmente un PagoReserva contra Bold. Útil cuando el
  // webhook no llegó. El endpoint hace self-heal: si Bold dice PAID, marca
  // el pago como pagado y procesa la confirmación igual que el webhook.
  const handleVerificarPasarela = async (pagoId) => {
    setAccionLoading(`pasarela-${pagoId}`);
    try {
      const res = await fetch(`${API_BASE}/turismo/pagos/${pagoId}/verificar-pasarela`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const body = await res.json();
      if (!res.ok) { onToast?.error?.(body.detail || 'Error consultando pasarela'); return; }
      if (body.cambio) {
        onToast?.success?.(body.mensaje || 'Estado actualizado');
      } else {
        onToast?.info?.(body.mensaje || 'Sin cambios');
      }
      onReload?.();
    } catch { onToast?.error?.('Error de red'); }
    finally { setAccionLoading(null); }
  };

  const handleDescargarPdf = () => {
    // Usamos un fetch con auth headers porque el endpoint requiere autenticación
    // y luego abrimos el blob — abrir directo no manda el token.
    fetch(`${API_BASE}/turismo/reservaciones/${reserva.id}/movimientos.pdf`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Liberar la URL después de un rato para que la pestaña tenga tiempo de cargar
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      })
      .catch(() => { /* errores se ven en la pestaña abierta */ });
  };

  return (
    <Stack spacing={1}>
      {/* Alerta bloqueante: reserva con voucher pendiente por subsanar.
          Aparece cuando la reserva tuvo un pago no-efectivo pero no se subió
          voucher válido. Bloquea operaciones (embarque, pagos, edición) hasta
          que se suba. Solo se permite subir el voucher desde el ⋮. */}
      {reserva?.voucher_pendiente_subsanar && (
        <Alert severity="error" icon={false}
          sx={{ fontSize: 12.5, py: 0.75, fontWeight: 700, border: '2px solid #dc2626',
                '& .MuiAlert-message': { fontSize: 12.5, width: '100%' } }}>
          ⚠️ VOUCHER PENDIENTE POR SUBSANAR — Esta reserva registró un pago con método no-efectivo pero no tiene voucher válido. Operaciones bloqueadas hasta subir el voucher.
        </Alert>
      )}

      {/* Resumen ejecutivo — una línea con el estado de la reserva */}
      {resumen?.texto && (
        <Alert severity={resumen.color || 'info'} sx={{ fontSize: 12, py: 0.5, '& .MuiAlert-message': { fontSize: 12, fontWeight: 600 } }}>
          {resumen.texto}
        </Alert>
      )}

      {/* Alertas contextuales — solo aparecen cuando hay algo que requiere acción
          (voucher pendiente >24h, saldo cerca de la salida, comisión sin liquidar,
          link Bold sin pago). Si está vacío no se renderiza nada. */}
      {alertas.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.5 }}>
          {alertas.map(a => (
            <Alert key={a.id} severity={a.severity || 'info'}
              sx={{ fontSize: 11.5, py: 0.25, '& .MuiAlert-message': { fontSize: 11.5 } }}>
              {a.texto}
            </Alert>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, gap: 1 }}>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Todas las transacciones de la reserva
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={handleDescargarPdf}
            sx={{ textTransform: 'none', fontSize: 12, color: '#475569' }}>
            PDF
          </Button>
          {Number(reserva.saldo_pendiente || 0) > 0 && (
            <Button size="small" startIcon={<LinkIcon />} onClick={onGenerarLink}
              sx={{ textTransform: 'none', fontSize: 12, color: '#0d9488' }}>
              Generar link Bold
            </Button>
          )}
        </Box>
      </Box>

      {/* Desglose financiero principal — Neto Entretur vs Comisión Agencia */}
      {f.precio_total > 0 && (
        <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Desglose financiero
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>PVP (precio de lista)</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
              {formatPrecio(f.precio_total, reserva.moneda)}
            </Typography>
          </Box>

          {/* Desglose de descuentos — solo se muestran si > 0 */}
          {Number(f.descuento_comercial || 0) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, pl: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: '#f59e0b' }}>− Descuento comercial (Entretur)</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#f59e0b' }}>
                − {formatPrecio(f.descuento_comercial, reserva.moneda)}
              </Typography>
            </Box>
          )}
          {Number(f.descuento_comision || 0) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5, pl: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: '#f59e0b' }}>− Descuento sobre comisión (agencia)</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#f59e0b' }}>
                − {formatPrecio(f.descuento_comision, reserva.moneda)}
              </Typography>
            </Box>
          )}

          {/* Cliente pagó — solo si hubo descuentos que hicieran diferencia */}
          {(Number(f.descuento_comercial || 0) > 0 || Number(f.descuento_comision || 0) > 0) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
              <Typography sx={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>Cliente pagó</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>
                {formatPrecio(f.precio_cliente, reserva.moneda)}
              </Typography>
            </Box>
          )}

          {/* Comisión efectiva de agencia — solo si aplica */}
          {Number(f.comision_efectiva || 0) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, pl: 1 }}>
              <Typography sx={{ fontSize: 11.5, color: '#7c3aed' }}>− Comisión de la agencia</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#7c3aed' }}>
                − {formatPrecio(f.comision_efectiva, reserva.moneda)}
              </Typography>
            </Box>
          )}

          {/* Neto Entretur */}
          <Box sx={{ pl: 1, borderLeft: `3px solid ${f.neto_anulado ? '#94a3b8' : '#0d9488'}`,
                     mb: 1, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700,
                                color: f.neto_anulado ? '#64748b' : '#0d9488',
                                textTransform: 'uppercase', letterSpacing: 0.3 }}>
                = Neto para Entretur
              </Typography>
              {/* Cuando el neto se anuló se muestra tachado el valor que
                  habría tenido: así se ve que existió y que se revirtió, en
                  vez de un cero sin historia. */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                {f.neto_anulado && (
                  <Typography sx={{ fontSize: 12, color: '#94a3b8',
                                    textDecoration: 'line-through' }}>
                    {formatPrecio(f.precio_cliente, reserva.moneda)}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 15, fontWeight: 800,
                                  color: f.neto_anulado ? '#64748b' : '#0d9488' }}>
                  {formatPrecio(f.neto_entretur, reserva.moneda)}
                </Typography>
              </Box>
            </Box>
            {f.neto_anulado && (
              <Typography sx={{ fontSize: 10.5, color: '#b45309', mt: 0.25, fontWeight: 700 }}>
                ↩ {f.neto_anulado_motivo}
              </Typography>
            )}
            {/* La agencia ya entregó esta plata en otra transferencia: en vez
                de pedirle un pago nuevo, se aplica lo que tiene a favor. Vale
                igual si el saldo es CxC suyo o cobro en muelle. */}
            {Number(reserva.saldo_pendiente || 0) > 0
              && Number(saldoFavor.saldo_a_favor || 0) > 0 && puedeCorregirSaldo && (
              <Button
                size="small" startIcon={<SavingsIcon />}
                onClick={() => setAplicarFavorOpen(true)}
                sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                      color: '#15803d', px: 0.5, mt: 0.25 }}
              >
                Aplicar saldo a favor · {formatPrecio(saldoFavor.saldo_a_favor, reserva.moneda)} disponibles
              </Button>
            )}
            {Number(reserva.saldo_pendiente || 0) > 0 && (
              reserva.saldo_a_cargo_de === 'agencia' && f.agencia_nombre ? (
                <>
                  <Typography sx={{ fontSize: 10.5, color: '#0891b2', mt: 0.25, fontWeight: 700 }}>
                    🧾 CxC generada · {f.agencia_nombre}: {formatPrecio(reserva.saldo_pendiente, reserva.moneda)}
                  </Typography>
                  {/* El pago de una venta a crédito llega después y llega como
                      una imagen. Por eso la acción está acá, pegada al saldo
                      que cierra, y no en otra pantalla. Solo se dibuja cuando
                      hay saldo Y es de la agencia. */}
                  {dentroDePlazo ? (
                    <Button
                      size="small" startIcon={<PaymentsIcon />}
                      onClick={() => setPagoAgenciaOpen(true)}
                      sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                            color: '#0d9488', px: 0.5, mt: 0.25 }}
                    >
                      Llegó el pago
                    </Button>
                  ) : (
                    /* Sin esta línea, el botón simplemente desaparece y el
                       coordinador no sabe si se dañó algo o dónde seguir. */
                    <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.25 }}>
                      El pasadía ya pasó · este pago se registra por cuentas por cobrar
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 10.5, color: '#dc2626', mt: 0.25 }}>
                    ⚠️ Aún por cobrar al cliente: {formatPrecio(reserva.saldo_pendiente, reserva.moneda)}
                  </Typography>
                  {/* El turista casi nunca paga el saldo completo en el muelle:
                      paga una parte y la agencia responde por el resto. Eso se
                      hacía a mano contra la base; ahora es este botón. Va acá,
                      pegado al saldo que resuelve. */}
                  {puedeCorregirSaldo && (
                    <Button
                      size="small" startIcon={<PaymentsIcon />}
                      onClick={() => setCobroMuelleOpen(true)}
                      sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                            color: '#0d9488', px: 0.5, mt: 0.25 }}
                    >
                      Repartir el saldo pendiente
                    </Button>
                  )}
                </>
              )
            )}
          </Box>

          {/* Comisión Agencia */}
          {f.estado_comision !== 'sin_comision' && f.comision_efectiva > 0 && (
            <Box sx={{ pl: 1, borderLeft: `3px solid ${estado.color}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: estado.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Comisión {f.agencia_nombre ? `· ${f.agencia_nombre}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: estado.color }}>
                  {formatPrecio(f.comision_efectiva, reserva.moneda)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, p: 0.75, borderRadius: 1, bgcolor: estado.bg }}>
                <Typography sx={{ fontSize: 13 }}>{estado.icon}</Typography>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: estado.color, flex: 1 }}>
                  {f.estado_comision_label}
                </Typography>
              </Box>
              {/* Desglose extra solo si hay algo que mostrar */}
              {f.comision_anticipada > 0 && (
                <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.5 }}>
                  • Ya cobrada por la agencia: {formatPrecio(f.comision_anticipada, reserva.moneda)}
                </Typography>
              )}
              {f.comision_pendiente > 0 && f.estado_comision !== 'liquidada' && (
                <Typography sx={{ fontSize: 10.5, color: '#dc2626', mt: 0.25 }}>
                  • Entretur le debe a la agencia: {formatPrecio(f.comision_pendiente, reserva.moneda)}
                </Typography>
              )}
              {f.descuento_comision > 0 && (
                <Typography sx={{ fontSize: 10.5, color: '#7c3aed', mt: 0.25 }}>
                  • Descuento que la agencia regaló al cliente: {formatPrecio(f.descuento_comision, reserva.moneda)}
                </Typography>
              )}
              {/* Reliquidar con otro neto. Va acá porque lo que mueve es este
                  número: el cliente paga lo mismo y cambia el reparto entre el
                  neto de Entretur y la comisión de la agencia. */}
              {puedeCorregirSaldo && (
                <Button
                  size="small" startIcon={<CalculateIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setReliquidarOpen(true)}
                  sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                        color: '#7c3aed', px: 0.5, mt: 0.5 }}
                >
                  Reliquidar con otro neto
                </Button>
              )}
              {/* La agencia vendedora equivocada. No mueve plata —todas
                  manejan la misma comisión—, solo corrige de quién es la
                  venta en los cinco sitios donde queda grabada. */}
              {puedeCorregirSaldo && reserva.agencia_id && (
                <Button
                  size="small" startIcon={<StorefrontIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setCambiarAgenciaOpen(true)}
                  sx={{ textTransform: 'none', fontSize: 11.5, fontWeight: 700,
                        color: '#c2410c', px: 0.5, mt: 0.25, display: 'block' }}
                >
                  Cambiar la agencia vendedora
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Totales operativos secundarios — solo lo que es != 0 */}
      {(t.cobros_muelle > 0 || t.voucher_pendiente > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 0.75, mb: 0.5 }}>
          {t.voucher_pendiente > 0 && (
            <ResumenChip label="Vouchers pendientes" valor={t.voucher_pendiente} color="#f59e0b" />
          )}
          {t.cobros_muelle > 0 && (
            <ResumenChip label="Cobrado en muelle" valor={t.cobros_muelle} color="#3b82f6" />
          )}
        </Box>
      )}

      {movimientos.length === 0 && (
        <Alert severity="info" sx={{ fontSize: 12 }}>
          No hay transacciones registradas para esta reserva.
        </Alert>
      )}

      {/* Filtro visual: ocultar del timeline los links de pasarela que nunca se
          usaron (saldo $0, cobrado por otra vía). El registro sigue en BD para
          trazabilidad — solo no lo mostramos porque no aporta info al operador
          y confunde con el precio viejo del link generado al crear la reserva. */}
      {movimientos
        .filter(m => {
          const saldoCero = Number(reserva.saldo_pendiente || 0) === 0;
          const linkHuerfano = m.tipo === 'pago_pasarela'
                               && (m.estado || '').toLowerCase() === 'pendiente'
                               && saldoCero;
          return !linkHuerfano;
        })
        .map((m, i) => {
        const visual = TIPO_VISUAL[m.tipo] || { icon: '•', label: m.tipo, color: '#64748b', bg: '#f8fafc' };
        const estadoColor = ESTADO_COLOR[(m.estado || '').toLowerCase()] || { bg: '#f1f5f9', fg: '#64748b' };
        const saldoCero = Number(reserva.saldo_pendiente || 0) === 0;
        const linkHuerfano = m.tipo === 'pago_pasarela' && (m.estado || '').toLowerCase() === 'pendiente' && saldoCero;
        return (
          <Box key={`${m.tipo}-${m.metadata?.voucher_id || m.metadata?.pago_id || m.metadata?.liquidacion_id || i}`}
            sx={{
              p: 1.25, borderRadius: 2,
              borderLeft: `3px solid ${visual.color}`,
              border: '1px solid #e2e8f0',
              bgcolor: linkHuerfano ? '#fafafa' : '#fff',
              opacity: linkHuerfano ? 0.75 : 1,
            }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 0.5, gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{ fontSize: 14 }}>{visual.icon}</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: visual.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {visual.label}
                  </Typography>
                  {m.subtipo && (
                    <Chip size="small" label={m.subtipo.replace(/_/g, ' ')}
                      sx={{ bgcolor: visual.bg, color: visual.color, fontWeight: 600, fontSize: 9, height: 16 }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  {m.descripcion}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>
                  {m.fecha ? new Date(m.fecha).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  {m.autor && ` · ${m.autor}`}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                    {formatPrecio(m.monto, reserva.moneda)}
                  </Typography>
                  <Chip size="small"
                    label={linkHuerfano ? 'no aplica' : (m.estado || '—')}
                    sx={{ bgcolor: linkHuerfano ? '#f1f5f9' : estadoColor.bg,
                          color: linkHuerfano ? '#64748b' : estadoColor.fg,
                          fontWeight: 600, fontSize: 9, height: 16, mt: 0.25 }} />
                </Box>
                {/* Corregir el movimiento: voucher o pago contable, sin bajar
                    a SQL. Restringido a admin/superadmin, que son operaciones
                    sobre evidencia de pago.
                    No se ofrece sobre comisiones ni cruces: son asientos
                    contables, no plata que entró a una cuenta, así que no
                    tienen cuenta destino, método ni referencia que corregir
                    —el diálogo salía vacío y el servidor los rechaza igual—.
                    La comisión se cambia reliquidando la reserva, no acá. */}
                {movimientoCorregible(m, puedeEditarMovimientos)
                 && (m.tipo === 'voucher' || puedeCorregirCuentaPago) && (
                  <Tooltip title={puedeCorregirMovimiento
                    ? 'Corregir este movimiento'
                    : `El plazo para corregir venció el ${_fmtDia(venceCorreccion)}: son `
                      + `${DIAS_PARA_CORREGIR} días después del pasadía.`}>
                    <span>
                      <IconButton size="small" disabled={!puedeCorregirMovimiento}
                        onClick={(e) => { e.stopPropagation(); setEditMov(m); }}
                        sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: '#334155' } }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Detalles adicionales según el tipo */}
            {m.referencia && (
              <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.25 }}>
                Ref: <span style={{ fontFamily: 'monospace' }}>{m.referencia}</span>
                {m.metadata?.ultimos_4_digitos && ` · Termina en ${m.metadata.ultimos_4_digitos}`}
              </Typography>
            )}
            {m.cuenta_destino && (
              <Typography sx={{ fontSize: 10.5, color: '#0891b2', mt: 0.25, fontWeight: 600 }}>
                🏦 Cuenta destino: {m.cuenta_destino}
              </Typography>
            )}
            {m.notas && (
              <Typography sx={{ fontSize: 10.5, color: '#475569', mt: 0.5, lineHeight: 1.4 }}>
                {m.notas}
              </Typography>
            )}
            {m.imagen_url && <ComprobanteThumb url={m.imagen_url} />}
            {m.payment_url && !linkHuerfano && (
              <Button size="small" startIcon={<OpenInNewIcon />}
                onClick={() => window.open(m.payment_url, '_blank')}
                sx={{ textTransform: 'none', fontSize: 11, mt: 0.5, color: '#3b82f6' }}>
                Abrir link
              </Button>
            )}
            {linkHuerfano && (
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', mt: 0.5 }}>
                Link de pasarela generado al crear la reserva — ya no aplica (saldo $0, cobrado por otra vía).
              </Typography>
            )}

            {/* Acciones por movimiento — solo aparecen cuando hay algo que el
                coordinador puede hacer desde aquí. No queremos saturar el card
                con botones inactivos. */}
            {m.tipo === 'voucher' && (m.estado || '').toLowerCase() === 'pendiente' && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
                <Button size="small" variant="contained"
                  disabled={accionLoading === `voucher-${m.metadata?.voucher_id}`}
                  onClick={() => handleVerificarVoucher(m.metadata?.voucher_id, 'aprobado')}
                  sx={{ textTransform: 'none', fontSize: 11, py: 0.25, bgcolor: '#10b981',
                        '&:hover': { bgcolor: '#059669' } }}>
                  Aprobar
                </Button>
                <Button size="small" variant="outlined"
                  disabled={accionLoading === `voucher-${m.metadata?.voucher_id}`}
                  onClick={() => handleVerificarVoucher(m.metadata?.voucher_id, 'rechazado')}
                  sx={{ textTransform: 'none', fontSize: 11, py: 0.25, borderColor: '#dc2626',
                        color: '#dc2626', '&:hover': { borderColor: '#b91c1c', bgcolor: '#fef2f2' } }}>
                  Rechazar
                </Button>
              </Box>
            )}
            {m.tipo === 'pago_pasarela' && !linkHuerfano && (m.estado || '').toLowerCase() === 'pendiente' && (
              <Box sx={{ mt: 0.75 }}>
                <Button size="small" variant="outlined"
                  disabled={accionLoading === `pasarela-${m.metadata?.pago_id}`}
                  onClick={() => handleVerificarPasarela(m.metadata?.pago_id)}
                  sx={{ textTransform: 'none', fontSize: 11, py: 0.25, borderColor: '#3b82f6',
                        color: '#3b82f6', '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' } }}>
                  {accionLoading === `pasarela-${m.metadata?.pago_id}`
                    ? 'Consultando…'
                    : 'Verificar en Bold'}
                </Button>
              </Box>
            )}
          </Box>
        );
      })}

      {/* Dialog de edición manual de vouchers y pagos */}
      <RegistrarPagoAgenciaDialog
        open={pagoAgenciaOpen}
        onClose={() => setPagoAgenciaOpen(false)}
        reserva={reserva}
        cuentasDestino={cuentasDestino}
        onToast={onToast}
        onHecho={onReload}
      />

      <CambiarAgenciaDialog
        open={cambiarAgenciaOpen}
        onClose={() => setCambiarAgenciaOpen(false)}
        reserva={reserva}
        onToast={onToast}
        onHecho={onReload}
      />

      <ReliquidarNetoDialog
        open={reliquidarOpen}
        onClose={() => setReliquidarOpen(false)}
        reserva={reserva}
        resumenFinanciero={f}
        onToast={onToast}
        onHecho={onReload}
      />

      <AplicarSaldoFavorDialog
        open={aplicarFavorOpen}
        onClose={() => setAplicarFavorOpen(false)}
        reserva={reserva}
        disponible={saldoFavor.saldo_a_favor}
        origenes={saldoFavor.origenes}
        onToast={onToast}
        onHecho={onReload}
      />

      <CobroMuelleCxCDialog
        open={cobroMuelleOpen}
        onClose={() => setCobroMuelleOpen(false)}
        reserva={reserva}
        cuentasDestino={cuentasDestino}
        onToast={onToast}
        onHecho={onReload}
      />

      <EditarMovimientoDialog
        open={!!editMov}
        onClose={() => setEditMov(null)}
        movimiento={editMov}
        cuentasDestino={cuentasDestino}

        usuario={getUserInfo()}
        onSaved={() => {
          onToast?.success('Movimiento actualizado');
          onReload?.();
        }}
      />
    </Stack>
  );
}

/**
 * ComprobanteThumb — Miniatura del voucher subido. Si es imagen muestra el
 * thumbnail; si es PDF muestra un placeholder con icono. Click → abre el
 * original en una pestaña nueva tamaño completo.
 */
function ComprobanteThumb({ url }) {
  if (!url) return null;
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const lower = url.toLowerCase();
  const esPdf = lower.endsWith('.pdf');
  const esImg = /\.(jpe?g|png|gif|webp|bmp)$/i.test(lower);
  return (
    <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box onClick={() => window.open(fullUrl, '_blank')}
        sx={{
          width: 80, height: 80, borderRadius: 1.5, overflow: 'hidden',
          border: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0,
          cursor: 'pointer', position: 'relative',
          transition: 'transform 0.15s, box-shadow 0.15s',
          '&:hover': { transform: 'scale(1.03)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
        }}>
        {esImg ? (
          <img src={fullUrl} alt="Comprobante"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : esPdf ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                     alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
            <Typography sx={{ fontSize: 24 }}>📄</Typography>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#64748b' }}>PDF</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            <Typography sx={{ fontSize: 24 }}>📎</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 700 }}>
          Comprobante adjunto
        </Typography>
        <Button size="small" startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => window.open(fullUrl, '_blank')}
          sx={{ textTransform: 'none', fontSize: 10.5, color: '#3b82f6', p: 0, minWidth: 0, justifyContent: 'flex-start' }}>
          Ver tamaño real
        </Button>
      </Box>
    </Box>
  );
}

function ResumenChip({ label, valor, color }) {
  return (
    <Box sx={{ p: 0.75, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color }}>
        {formatPrecio(valor)}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------------
// Diálogo de viajero
// ----------------------------------------------------------------------------

function emptyViajero() {
  return {
    nombre_completo: '', tipo_documento: 'CC', numero_documento: '',
    fecha_nacimiento: '', es_menor: false, es_contacto_principal: false,
    telefono_contacto: '', requerimientos_especiales: '',
  };
}

function toForm(v) {
  return {
    nombre_completo: v.nombre_completo || '',
    tipo_documento: v.tipo_documento || 'CC',
    numero_documento: v.numero_documento || '',
    fecha_nacimiento: v.fecha_nacimiento || '',
    es_menor: !!v.es_menor,
    es_contacto_principal: !!v.es_contacto_principal,
    telefono_contacto: v.telefono_contacto || '',
    requerimientos_especiales: v.requerimientos_especiales || '',
  };
}

function ViajeroDialog({ editando, form, onChange, onSave, onClose }) {
  return (
    <SlideOver open onClose={onClose}
      title={editando ? 'Editar viajero' : 'Nuevo viajero'}
      icon={<GroupAddIcon />}
      footer={
        <>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" disableElevation onClick={onSave}
            disabled={!form.nombre_completo.trim()}
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            {editando ? 'Guardar' : 'Agregar'}
          </Button>
        </>
      }
    >
      <Stack spacing={1.5}>
        <TextField size="small" label="Nombre completo *" value={form.nombre_completo}
          onChange={e => onChange({ ...form, nombre_completo: e.target.value })} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 1 }}>
          <TextField size="small" select label="Documento" value={form.tipo_documento}
            onChange={e => onChange({ ...form, tipo_documento: e.target.value })}>
            <MenuItem value="CC">CC</MenuItem>
            <MenuItem value="TI">TI</MenuItem>
            <MenuItem value="CE">CE</MenuItem>
            <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
          </TextField>
          <TextField size="small" label="Número" value={form.numero_documento}
            onChange={e => onChange({ ...form, numero_documento: e.target.value })} />
        </Box>
        <TextField size="small" type="date" label="Fecha de nacimiento" value={form.fecha_nacimiento}
          onChange={e => onChange({ ...form, fecha_nacimiento: e.target.value })}
          InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Teléfono contacto" value={form.telefono_contacto}
          onChange={e => onChange({ ...form, telefono_contacto: e.target.value })} />
        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            <input type="checkbox" checked={form.es_contacto_principal}
              onChange={e => onChange({ ...form, es_contacto_principal: e.target.checked })} />
            <Typography component="span" sx={{ ml: 0.5, fontSize: 12 }}>Contacto principal</Typography>
          </Box>
          <Box>
            <input type="checkbox" checked={form.es_menor}
              onChange={e => onChange({ ...form, es_menor: e.target.checked })} />
            <Typography component="span" sx={{ ml: 0.5, fontSize: 12 }}>Es menor de edad</Typography>
          </Box>
        </Box>
        <TextField size="small" multiline minRows={2} label="Requerimientos especiales"
          value={form.requerimientos_especiales}
          onChange={e => onChange({ ...form, requerimientos_especiales: e.target.value })} />
      </Stack>
    </SlideOver>
  );
}

// Tab Evidencia — muestra las fotos tomadas en muelle durante el checkin.
// Cada evidencia trae: rol (titular/acompañante), nombre capturado, foto,
// tipo (documento/persona), datos OCR si existen, coordinador que la tomó.
// Las fotos son sensibles (documentos personales); se muestran aquí con
// preview clickeable que abre la imagen completa en nueva pestaña.
export function TabEvidencia({ data, onReload, onToast }) {
  const evidencias = data?.evidencias || [];
  const [ocrLoading, setOcrLoading] = useState(null); // id de la evidencia procesándose

  const handleExtraerIA = async (evidenciaId) => {
    setOcrLoading(evidenciaId);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones/checkin-evidencia/${evidenciaId}/ocr`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await r.json();
      if (!r.ok || !body.ok) {
        onToast?.error?.(body.error || body.detail || 'No se pudo extraer con IA');
      } else if (body.nombre_capturado) {
        onToast?.success?.(`Nombre extraído: ${body.nombre_capturado}`);
      } else {
        onToast?.info?.('OCR completado sin nombre reconocible');
      }
      onReload?.();
    } catch (e) {
      onToast?.error?.('Error de red al ejecutar OCR');
    } finally {
      setOcrLoading(null);
    }
  };

  if (evidencias.length === 0) {
    return (
      <Alert severity="info" sx={{ fontSize: 12 }}>
        Aún no hay evidencia de checkin en muelle. Se registra cuando el equipo del muelle captura la foto del titular/acompañantes.
      </Alert>
    );
  }

  const _formatFecha = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-CO', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const _tipoFotoLabel = (tipo) => {
    if (tipo === 'documento') return { label: 'Documento', color: '#0891b2', bg: '#ecfeff' };
    if (tipo === 'persona')   return { label: 'Persona',   color: '#f59e0b', bg: '#fffbeb' };
    return { label: '—', color: '#94a3b8', bg: '#f8fafc' };
  };

  const _rolLabel = (rol) => {
    if (rol === 'titular') return { label: 'Titular', color: '#0d9488', bg: '#f0fdfa' };
    return { label: (rol || 'acompañante'), color: '#6366f1', bg: '#eef2ff' };
  };

  return (
    <Stack spacing={1.25}>
      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
        {evidencias.length} evidencia{evidencias.length === 1 ? '' : 's'} capturada{evidencias.length === 1 ? '' : 's'} en muelle.
        Click en la imagen para verla completa.
      </Typography>

      {evidencias.map(ev => {
        const tipoV = _tipoFotoLabel(ev.tipo_foto);
        const rolV = _rolLabel(ev.rol);
        return (
          <Box key={ev.id} sx={{
            p: 1.25, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
            display: 'flex', gap: 1.25,
          }}>
            {/* Thumbnail clickeable — abre la imagen completa en nueva pestaña.
                foto_url llega relativa (/static/...); necesita el prefijo API_BASE
                porque el frontend corre en otro puerto que el backend. */}
            {ev.foto_url ? (() => {
              const fullUrl = ev.foto_url.startsWith('http') ? ev.foto_url : `${API_BASE}${ev.foto_url}`;
              return (
                <Box
                  onClick={() => window.open(fullUrl, '_blank')}
                  sx={{
                    width: 96, height: 96, borderRadius: 1.5, overflow: 'hidden',
                    cursor: 'pointer', flexShrink: 0, border: '1px solid #e2e8f0',
                    transition: 'transform 0.15s',
                    '&:hover': { transform: 'scale(1.03)', borderColor: '#0d9488' },
                  }}
                >
                  <img
                    src={fullUrl}
                    alt={`Evidencia ${ev.rol}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              );
            })() : (
              <Box sx={{
                width: 96, height: 96, borderRadius: 1.5, flexShrink: 0,
                bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#94a3b8', fontSize: 32,
              }}>
                📷
              </Box>
            )}

            {/* Datos de la evidencia */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={rolV.label}
                  sx={{ bgcolor: rolV.bg, color: rolV.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                {ev.tipo_foto && (
                  <Chip size="small" label={tipoV.label}
                    sx={{ bgcolor: tipoV.bg, color: tipoV.color, fontWeight: 700, fontSize: 10, height: 18 }} />
                )}
                {ev.doc_vigente === false && (
                  <Chip size="small" label="⚠️ Doc vencido"
                    sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 18 }} />
                )}
                {ev.ocr_coincide_titular === false && (
                  <Chip size="small" label="⚠️ No coincide titular"
                    sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 700, fontSize: 10, height: 18 }} />
                )}
                {ev.anonimizada_at && (
                  <Chip size="small" label="🔒 Anonimizada"
                    sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: 10, height: 18 }} />
                )}
              </Box>

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                {ev.nombre_capturado || (ev.rol === 'titular' ? 'Titular (sin nombre capturado)' : 'Sin nombre capturado')}
              </Typography>

              {(ev.documento_tipo || ev.documento_numero) && (
                <Typography sx={{ fontSize: 11.5, color: '#475569', mt: 0.25 }}>
                  {ev.documento_tipo && <>{ev.documento_tipo.toUpperCase()}</>}
                  {ev.documento_tipo && ev.documento_numero && ' · '}
                  {ev.documento_numero && <span style={{ fontFamily: 'monospace' }}>{ev.documento_numero}</span>}
                  {ev.nacionalidad && ` · ${ev.nacionalidad}`}
                </Typography>
              )}

              {ev.motivo_omision && (
                <Typography sx={{ fontSize: 11.5, color: '#dc2626', mt: 0.5, fontStyle: 'italic' }}>
                  Omitida: {ev.motivo_omision}
                  {ev.notas_omision && ` — ${ev.notas_omision}`}
                </Typography>
              )}

              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.5 }}>
                {_formatFecha(ev.foto_subida_at || ev.created_at)}
                {ev.capturada_por_nombre && ` · ${ev.capturada_por_nombre}`}
              </Typography>

              {/* Botón "Extraer con IA" — solo para fotos de documento sin
                  nombre capturado. Llama Claude Vision (Haiku 4.5) y actualiza
                  los campos vacíos. Costo aprox: $0.001 por foto. */}
              {ev.tipo_foto === 'documento'
                && !ev.nombre_capturado
                && !ev.anonimizada_at
                && ev.foto_url && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={ocrLoading === ev.id}
                  onClick={() => handleExtraerIA(ev.id)}
                  sx={{
                    mt: 0.75, textTransform: 'none', fontSize: 11, py: 0.25,
                    borderColor: '#8b5cf6', color: '#8b5cf6',
                    '&:hover': { borderColor: '#7c3aed', bgcolor: '#faf5ff' },
                  }}
                >
                  {ocrLoading === ev.id ? (
                    <>
                      <CircularProgress size={11} sx={{ mr: 0.75, color: '#8b5cf6' }} />
                      Analizando…
                    </>
                  ) : (
                    <>🪄 Extraer con IA</>
                  )}
                </Button>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
